function extractSectionId(element) {
  element = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
  return element.id.replace("shopify-section-", "");
}

function initHpProductTabs() {
  document.querySelectorAll("hp-product-tabs").forEach((tabs) => observeRecommendations(tabs));
}

class HpProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-hp-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-hp-tab-panel]"));

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.activateTab(tab.getAttribute("data-hp-tab"), true));
    });

    this.addEventListener("hp-product-tabs:panel-updated", () => this.handlePanelUpdate());
    this.activateTab("recommended");
    this.handlePanelUpdate();
  }

  activateTab(tabId, scrollToTab = false) {
    this.tabs.forEach((tab) => {
      const isActive = tab.getAttribute("data-hp-tab") === tabId;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.setAttribute("aria-current", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-hp-tab-panel") === tabId;
      panel.hidden = !isActive;
    });

    if (scrollToTab && window.innerWidth < 700) {
      const activeTab = this.tabs.find((tab) => tab.getAttribute("data-hp-tab") === tabId);
      const navigation = activeTab?.closest('[role="tablist"]');
      if (!navigation) return;
      const tabBounds = activeTab.getBoundingClientRect();
      const navigationBounds = navigation.getBoundingClientRect();
      const targetLeft = navigation.scrollLeft + tabBounds.left - navigationBounds.left - (navigation.clientWidth - tabBounds.width) / 2;
      navigation.scrollTo({
        left: Math.max(0, Math.min(targetLeft, navigation.scrollWidth - navigation.clientWidth)),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }
  }

  panelHasProducts(panelName) {
    const panel = this.querySelector(`[data-hp-tab-panel="${panelName}"]`);
    if (!panel) {
      return false;
    }

    return panel.querySelector("product-list, scroll-carousel") !== null;
  }

  isRecentLoaded() {
    return this.querySelector("hp-recently-viewed-products")?.hasAttribute("data-loaded") === true;
  }

  isRecommendedSettled() {
    const recommendations = this.querySelector("hp-product-recommendations");
    return recommendations === null || recommendations.hasAttribute("data-loaded");
  }

  handlePanelUpdate() {
    const recentHasProducts = this.panelHasProducts("recent");
    const recommendedHasProducts = this.panelHasProducts("recommended");
    const recentLoaded = this.isRecentLoaded();
    const recommendedSettled = this.isRecommendedSettled();

    this.querySelector('[data-empty="recent"]')?.toggleAttribute("hidden", recentHasProducts || !recentLoaded);
    this.querySelector('[data-loading="recent"]')?.toggleAttribute("hidden", recentLoaded);

    this.querySelector('[data-empty="recommended"]')?.toggleAttribute("hidden", recommendedHasProducts || !recommendedSettled);
    this.querySelector('[data-loading="recommended"]')?.toggleAttribute("hidden", recommendedSettled || recommendedHasProducts);
  }
}

class HpRecentlyViewedProducts extends HTMLElement {
  #isLoaded = false;

  connectedCallback() {
    this.loadProducts();
  }

  #searchQueryString() {
    const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));

    if (this.hasAttribute("exclude-id")) {
      items.delete(parseInt(this.getAttribute("exclude-id"), 10));
    }

    return Array.from(items.values(), (item) => `id:${item}`)
      .slice(0, parseInt(this.getAttribute("products-count"), 10))
      .join(" OR ");
  }

  #finishLoad() {
    this.setAttribute("data-loaded", "");
    this.closest("hp-product-tabs")?.dispatchEvent(new CustomEvent("hp-product-tabs:panel-updated", { bubbles: true }));
  }

  async loadProducts() {
    if (this.#isLoaded) {
      return;
    }

    this.#isLoaded = true;

    const query = this.#searchQueryString();

    if (!query) {
      this.#finishLoad();
      return;
    }

    try {
      const section = this.closest(".shopify-section");
      const url = `${Shopify.routes.root}search?type=product&q=${query}&section_id=${extractSectionId(section)}`;
      const response = await fetch(url, { priority: "low" });
      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const recentlyViewedProductsElement = tempDiv.querySelector("hp-recently-viewed-products");

      if (recentlyViewedProductsElement?.childElementCount > 0) {
        this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
      }
    } catch (error) {
      console.warn("hp-recently-viewed-products:", error);
    }

    this.#finishLoad();
  }
}

class HpProductRecommendations extends HTMLElement {
  #isLoaded = false;

  connectedCallback() {
    this.loadRecommendations();
  }

  #resolveProductId() {
    const explicitProductId = this.getAttribute("product");
    if (explicitProductId) {
      return explicitProductId;
    }

    if (!this.hasAttribute("allow-recent-seed")) {
      return null;
    }

    const items = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
    return items.length > 0 ? String(items[0]) : null;
  }

  #finishLoad() {
    this.setAttribute("data-loaded", "");
    this.closest("hp-product-tabs")?.dispatchEvent(new CustomEvent("hp-product-tabs:panel-updated", { bubbles: true }));
  }

  async loadRecommendations() {
    if (this.#isLoaded) {
      return;
    }

    this.#isLoaded = true;

    const productId = this.#resolveProductId();

    if (!productId) {
      this.remove();
      this.#finishLoad();
      return;
    }

    if (!this.getAttribute("product")) {
      this.setAttribute("product", productId);
    }

    try {
      const section = this.closest(".shopify-section");
      const intent = this.getAttribute("intent") || "related";
      const limit = this.getAttribute("limit") || 9;
      const url = `${Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${extractSectionId(section)}&intent=${intent}`;
      const response = await fetch(url, { priority: "low" });
      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const recommendationsElement =
        tempDiv.querySelector("hp-product-recommendations") || tempDiv.querySelector("product-recommendations");

      if (recommendationsElement?.childElementCount > 0) {
        this.replaceChildren(...document.importNode(recommendationsElement, true).childNodes);
      } else {
        this.remove();
      }
    } catch (error) {
      console.warn("hp-product-recommendations:", error);
      this.remove();
    }

    this.#finishLoad();
  }
}

function observeRecommendations(root) {
  const recommendations = root.querySelector("hp-product-recommendations");
  const tabs = root.closest("hp-product-tabs");

  if (!tabs) {
    return;
  }

  const notify = () => tabs.dispatchEvent(new CustomEvent("hp-product-tabs:panel-updated", { bubbles: true }));

  if (!recommendations) {
    notify();
    return;
  }

  const observer = new MutationObserver(() => {
    notify();

    if (!recommendations.isConnected) {
      observer.disconnect();
    }
  });

  observer.observe(recommendations, { childList: true, subtree: true });
  observer.observe(recommendations.parentElement, { childList: true });

  setTimeout(() => {
    if (!recommendations.hasAttribute("data-loaded")) {
      recommendations.setAttribute("data-loaded", "");
      notify();
    }

    root.querySelector('[data-loading="recommended"]')?.setAttribute("hidden", "");
    notify();
  }, 5000);
}

if (!window.customElements.get("hp-product-tabs")) {
  window.customElements.define("hp-product-tabs", HpProductTabs);
}

if (!window.customElements.get("hp-recently-viewed-products")) {
  window.customElements.define("hp-recently-viewed-products", HpRecentlyViewedProducts);
}

if (!window.customElements.get("hp-product-recommendations")) {
  window.customElements.define("hp-product-recommendations", HpProductRecommendations);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHpProductTabs);
} else {
  initHpProductTabs();
}
